define('bindingHandlers/timeInput/TimeInputView',[
    'lib/lodash4',
    'lib/knockout',
    'lib/globalize',
    'moment',

    'util/keyCode',
    'viewModels/ViewModel',

    'extensions/i18n/knockout.i18n'

], function(
    _,
    ko,
    globalize,
    moment,

    keyCode,
    ViewModel

    ){
    "use strict";

    /**
     * ViewModel for the time input widget.
     */
    return ViewModel.extend( function( options ){
        var self = this,

            UNIQUE_ID = _.uniqueId('ti.'),

            SELECTION_HOURS   = ko.i18n.renderString('widget.timeinput.selected.hours'),
            SELECTION_MINUTES = ko.i18n.renderString('widget.timeinput.selected.minutes'),
            SELECTION_SECONDS = ko.i18n.renderString('widget.timeinput.selected.seconds'),
            SELECTION_AM_PM   = ko.i18n.renderString('widget.timeinput.selected.ampm'),

            is24HourTime = false,
            padHours     = false,
            showSeconds  = false,

            MIN_HOUR = 0,
            MAX_HOUR = 0,

            MIN_MINUTE_SEC = 0,
            MAX_MINUTE_SEC = 59,

            MIN_MERIDIEM = 0,
            MAX_MERIDIEM = 1,

            VALUES_MERIDIEM = [],

            clickedObservable,
            clearFollowing,
            currentSpinner,
            subscriptions = [],

            lastValue = null,

            valueIsMoment = false,
            valueIsString = false,

            /**
             * Clears subscriptions created when the input is configured
             */
            disposeSubscriptions = function(){
                _.each( subscriptions, function( subscription ){
                    subscription.dispose();
                });
            },

            /**
             * Takes the input from the config object and sets the internal flags according to the values passed therein
             * @param config
             */
            updateFromConfigObject = function( config ){
                config = config || {
                    momentFormat   : 'h:mm a',
                    useStringValue : true,
                    value          : ko.observable(''),
                    delimiter      : ':'
                };

                options = config;

                is24HourTime = (/H/).test( options.momentFormat );
                padHours     = (/hh/i).test( options.momentFormat );
                showSeconds  = (/ss/).test( options.momentFormat );

                MIN_HOUR = is24HourTime ? 0  : 1;
                MAX_HOUR = is24HourTime ? 23 : 12;

                VALUES_MERIDIEM = options.meridiemValues || [];

                self.showSeconds( showSeconds );
                self.showMeridiem( !is24HourTime );
                self.delimiter( options.delimiter);

                if (!_.isUndefined(options.allowClear)){
                    self.allowClear(options.allowClear);
                }

            },

            /**
             * Updates the bindable value object with the current time based on the changes to the inputs
             */
            updateTimeValue = function(){
                var hours    = parseInt( self.hours(),   10 ),
                    minutes  = parseInt( self.minutes(), 10 ),
                    seconds  = parseInt( self.seconds(), 10 ),

                    meridiem = is24HourTime ? null : self.meridiem(),
                    valueMoment = valueIsMoment ? options.value() : moment.utc(),
                    isPM = false;

                if ( !is24HourTime  ) {
                    isPM = VALUES_MERIDIEM.indexOf( meridiem ) === 1;

                    if ( hours === MAX_HOUR ) {
                        hours = isPM ? hours : hours - MAX_HOUR;
                    } else if ( isPM ){
                        hours += MAX_HOUR;
                    }
                }

                if ( isNaN( hours ) && isNaN( minutes ) && isNaN( seconds ) ) {
                    if ( valueIsMoment ) {
                        options.value( moment.utc() );
                    } else if ( valueIsString ) {
                        options.value('');
                    }
                } else {
                    valueMoment.hours(     hours || 0 );
                    valueMoment.minutes( minutes || 0 );
                    valueMoment.seconds( seconds || 0 );

                    if ( valueIsMoment ) {
                        options.value.valueHasMutated();
                    } else if ( valueIsString ) {
                        options.value( valueMoment.format( 'HH:mm:ss' ));
                    }
                }

                lastValue = options.value();
            },

            isolateFocus = function(observers, targetObserver){
                return function(value){
                    if (value) {
                        _.each(observers, function(observer){
                           if (observer !== targetObserver){
                               observer(false);
                           }
                        });
                    }
                };
            },

            configureSubscriptions = function(){

                // clearing any existing subscriptions in case this config was called more than once
                disposeSubscriptions();

                var focusable = [
                    self.focusHours,
                    self.focusMinutes,
                    self.focusSeconds,
                    self.focusMeridiem
                ];

                subscriptions = [
                    self.hours.subscribe(updateTimeValue),
                    self.minutes.subscribe(updateTimeValue),
                    self.seconds.subscribe(updateTimeValue),
                    self.meridiem.subscribe(updateTimeValue)
                ];

                subscriptions = subscriptions.concat(
                    _.map(focusable, function(observer){
                        return observer.subscribe(isolateFocus(focusable, observer));
                    })
                );
            },

            /**
             * Gets the input time as a string or a moment object and sets the values of the widget based on that time.
             * If the time is a string it has to be in the format of 'HH:mm:ss'.  If it is a moment it should be in utc
             * but the time output will remain whatever timezone offset you have defined.
             */
            configureFromTimeValue = function(val){
                if ( lastValue === val ){
                    return ;
                }

                valueIsString = options.useStringValue || _.isString( val );
                valueIsMoment = !valueIsString;

                if ( _.isUndefined( val ) || _.isNull( val ) ) {

                    if ( valueIsMoment ) {
                        options.value( moment.utc() );
                    } else if ( valueIsString ) {
                        options.value( '' );
                        self.onClearInputClick();
                    }

                } else if ( val.length === 0 ) {
                    self.onClearInputClick();
                } else if ( val.length !== 0) {
                    val = valueIsString ? moment(ko.unwrap(val), 'HH:mm:ss:SSS') : moment(val);


                    // check if this is a locale that uses a 12-hour clock
                    if (self.showMeridiem()) {
                        var isAm = moment(val).locale('en-US').format('a').indexOf('a') > -1;
                        self.meridiem(VALUES_MERIDIEM[ isAm ? 0 : 1 ]);
                        self.hours(val.format('h'));
                    } else {
                        self.hours(val.format('H'));
                    }

                    self.minutes(val.format('mm'));
                    self.seconds(val.format('ss'));
                }

                configureSubscriptions();
            },



            /**
             * Responds to the click of the spinners based on the currently selected input item
             * @type {Function}
             */
            spin = _.throttle( function( dir ){
                self.updateCurrentSpinner();
                if ( currentSpinner ) {
                    currentSpinner(dir);
                }
            }, 100 ),

            /**
             * Gets a leading padded zero value string
             * @param value
             * @returns {*}
             */
            getPaddedValue = function( value ) {
                if ( value < 10 ) {
                    return '0' + value;
                }
                return value;
            },

            /**
             * Updates the observable based on the value passed in bound by the min and the max.  If the observable value
             * is greater than the max, the method retains the current and returns true.  This response is evaluated
             * below and will result in moving the focus to the next column and inserting that value or ignoring the input.
             *
             * @param observable
             * @param keyValue
             * @param min
             * @param max
             * @returns {boolean}
             */
            updateValueString = function( observable, keyValue, min, max ) {
                var currentValue, newValue, valueString,
                    pad = ( observable !== self.hours || padHours );

                if ( clickedObservable === observable ) {
                    observable(null);
                    clickedObservable = null;
                    clearFollowing = true;
                }

                currentValue = parseInt( observable(), 10 );
                currentValue = isNaN( currentValue ) ? 0 : currentValue;
                valueString  = currentValue.toString() + keyValue.toString();
                newValue     = parseInt( valueString, 10 );

                if (isNaN(newValue) || keyValue === -1) {
                    return false;
                }
                if ( newValue > max ){
                    return true;
                }

                newValue = Math.max(min, newValue);
                observable( pad ? getPaddedValue( newValue ) : newValue );

                return false;
            },

            /**
             * Advances the focus to the next input column as specified by the passed in parameters as well as sets
             * the value in the focused column to be clearable by the next valid input.
             * @param showValue
             * @param nextObservable
             * @param nextFocus
             * @returns {boolean}
             */
            setNextFocusIfPossible = function( showValue, nextObservable, nextFocus ){
                if ( showValue ) {
                    if (clearFollowing) {
                        clickedObservable = nextObservable;
                        clearFollowing = false;
                    }
                    nextFocus(true);

                    return true;
                }
                return false;
            },

            /**
             * Sets a value specified by the valueObservable based on the passed in offset bound by the min and max bounds.
             * If the values array is populated, the values are toggled between the 2 values available in the values array.
             * This is used primarily for spinner, arrow and scroll responses.
             *
             * @param valueOffset
             * @param valueObservable
             * @param min
             * @param max
             * @param values
             */
            setValueWithLimits = function( valueOffset, valueObservable, min, max, values ) {
                if ( valueObservable ) {
                    var stringValue = valueObservable(),
                        numberValue = parseInt( stringValue, 10 ),
                        pad = ( valueObservable !== self.hours || padHours ),
                        newValue;

                    if ( isNaN(numberValue) ) {
                        numberValue = values && stringValue ? values.indexOf( stringValue ) : 0;
                    }
                    newValue = numberValue + valueOffset;

                    if ( newValue > max ) {
                        newValue = min;
                    } else if ( newValue < min ) {
                        newValue = max;
                    }

                    if (values) {
                        stringValue = values[newValue];
                    } else {
                        stringValue = pad ? getPaddedValue( newValue ) : newValue;
                    }
                    valueObservable(stringValue);
                }
            },

            /**
             * Gets a spinner function that is activated with the currently focused element so that it changes that
             * element on spinner click and resets the focus to that input after click.
             *
             * @param valueObservable
             * @param min
             * @param max
             * @param focusAfter
             * @param values
             * @returns {Function}
             */
            getSpinner = function( valueObservable, min, max, focusAfter, values ) {
                return function ( spinVector ) {
                    setValueWithLimits( spinVector, valueObservable, min, max, values );
                    if ( focusAfter ) {
                        focusAfter(true);
                    }
                };
            },

            /**
             * Evaluates the passed in keyCode to get the character representation of the pressed key.  If the key
             * pressed is unique in the am/pm string, the value will be set to the appropriate entry.  For instance
             * the strings "AM" and "PM" have "A" or "P" unique so selection of either of those characters will
             * trigger the setting of the meridiem value.  For most locales this is immediately clear but for locales like
             * farsi that have the second character as the determining character - the evaluation is not made until
             * the character is set.
             * @param keyPressed
             * @returns {*}
             */
            getMeridiemForKeyPressed = function( keyPressed ){
                var charStr = String.fromCharCode(keyPressed),
                    amString = VALUES_MERIDIEM[0] || '',
                    amStringLength = amString.length,
                    pmString = VALUES_MERIDIEM[1] || '',
                    currentChar = '',
                    meridiem = null, i;


                for( i = amStringLength - 1; i >= 0; i-- ) {
                    currentChar = amString.charAt( i );

                    if ( pmString.lastIndexOf( currentChar ) !== -1 ){
                        pmString = pmString.replace( currentChar, '' );
                        amString = amString.replace( currentChar, '' );
                    }
                }


                if ( amString.toLowerCase().indexOf( charStr.toLowerCase() ) !== -1 ) {

                    meridiem = 0;

                } else if ( pmString.toLowerCase().indexOf( charStr.toLowerCase() ) !== -1 ) {

                    meridiem = 1;

                }

                return meridiem;
            },

            /**
             * gets the up and down directions based on the passed in keyPress if applicable
             * @param keyPressed
             * @returns {*}
             */
            getDirectionForKeyPressed = function( keyPressed ){
                var direction;
                if ( keyPressed === keyCode.UP     ||
                    keyPressed === keyCode.PAGE_UP ||
                    keyPressed === keyCode.HOME ) {

                    direction = 1;

                } else if ( keyPressed === keyCode.DOWN ||
                    keyPressed === keyCode.PAGE_DOWN    ||
                    keyPressed === keyCode.END ) {

                    direction = -1;

                }
                return direction;
            },

            /**
             * Handles the various ways the user can clear the currently focused input
             * @param keyPressed
             * @returns {boolean}
             */
            handleClearFocused = function( keyPressed ){
                if ( keyPressed === keyCode.BACKSPACE ||
                    keyPressed === keyCode.DELETE ) {

                    if (self.focusHours()) {
                        self.hours(null);
                    } else if (self.focusMinutes()) {
                        self.minutes(null);
                    } else if (self.focusSeconds()) {
                        self.seconds(null);
                    } else if (self.focusMeridiem()) {
                        self.meridiem(null);
                    }
                    return true;
                }
                return false;
            },

            /**
             * Clears the clicked observable if there are no inputs with focus
             */
            clearedClickedObservable = function(){
                var hasInnerFocus =
                    self.focusHours()    ||
                    self.focusMinutes()  ||
                    self.focusSeconds()  ||
                    self.focusMeridiem();

                if ( !hasInnerFocus ) {
                    clickedObservable = null;
                    clearFollowing = false;
                }

            },

            /**
             * handles input entries by either the passed in value, the direction vector, or the meridian index
             * @param keyValue
             * @param direction
             * @param meridiem
             */
            handleKeyInteraction = function( keyValue, direction, meridiem ){

                var useDirection       = direction < 0  || direction > 0,
                    meridiemKeyPressed = meridiem === 0 || meridiem === 1;

                if ( keyValue !== -1 || meridiemKeyPressed || useDirection ) {
                    if (self.focusHours() && !meridiemKeyPressed ) {
                        if (useDirection) {
                            setValueWithLimits(direction, self.hours, MIN_HOUR, MAX_HOUR);
                            return;
                        }

                        if (updateValueString(self.hours, keyValue, MIN_HOUR, MAX_HOUR)) {

                            if (!setNextFocusIfPossible(self.showMinutes(), self.minutes, self.focusMinutes)) {
                                if (!setNextFocusIfPossible(self.showSeconds(), self.seconds, self.focusSeconds)) {
                                    setNextFocusIfPossible(self.showMeridiem(), self.meridiem, self.focusMeridiem);
                                }
                            }
                        }
                    }

                    if (self.focusMinutes() || self.focusSeconds()) {
                        if (useDirection) {
                            setValueWithLimits(direction, self.focusMinutes() ? self.minutes : self.seconds, MIN_MINUTE_SEC, MAX_MINUTE_SEC);
                            return;
                        }

                        if (self.focusMinutes()) {
                            if (updateValueString(self.minutes, keyValue, MIN_MINUTE_SEC, MAX_MINUTE_SEC) || meridiemKeyPressed) {

                                if (!setNextFocusIfPossible(self.showSeconds(), self.seconds, self.focusSeconds)) {
                                    setNextFocusIfPossible(self.showMeridiem(), self.meridiem, self.focusMeridiem);
                                }
                            }
                        }

                        if (self.focusSeconds()) {
                            if (( updateValueString(self.seconds, keyValue, MIN_MINUTE_SEC, MAX_MINUTE_SEC) || meridiemKeyPressed )) {
                                setNextFocusIfPossible(self.showMeridiem(), self.meridiem, self.focusMeridiem);
                            }
                        }
                    }

                    if (self.focusMeridiem()) {
                        if (useDirection) {
                            setValueWithLimits(direction, self.meridiem, MIN_MERIDIEM, MAX_MERIDIEM, VALUES_MERIDIEM);
                        } else {
                            self.meridiem(VALUES_MERIDIEM[meridiem]);
                        }
                    }
                }
            };

        self.hours             = ko.observable();
        self.showHours         = ko.observable(true);
        self.focusHours        = ko.observable(false);
        self.hourID            = ko.observable( 'hours' + UNIQUE_ID );

        self.minutes           = ko.observable();
        self.showMinutes       = ko.observable(true);
        self.focusMinutes      = ko.observable(false);
        self.minuteId          = ko.observable( 'minutes' + UNIQUE_ID );

        self.seconds           = ko.observable();
        self.showSeconds       = ko.observable(true);
        self.focusSeconds      = ko.observable(false);
        self.secondId          = ko.observable( 'seconds' + UNIQUE_ID );

        self.meridiem          = ko.observable();
        self.showMeridiem      = ko.observable(true);
        self.focusMeridiem     = ko.observable(false);
        self.meridiemId        = ko.observable( 'meridiem' + UNIQUE_ID );

        self.clearInputFocused = ko.observable(false);
        self.spinDownFocused   = ko.observable(false);
        self.spinUpFocused     = ko.observable(false);

        self.overClear         = ko.observable(false);

        self.currentSelection  = ko.observable();
        self.delimiter         = ko.observable();
        self.isDisabled        = ko.observable(false);

        self.timeInputId       = ko.observable( 'timeinput' + UNIQUE_ID );

        self.allowClear = ko.observable(_.isUndefined(options) || _.isUndefined(options.allowClear) || !!options.allowClear);

        /**
         * When focus changes this sets a spinner method to respond to clicked changes by the spinners.
         */
        self.updateCurrentSpinner = self.computed( function(){

            if ( self.focusHours() ) {
                currentSpinner = getSpinner( self.hours, MIN_HOUR, MAX_HOUR, self.focusHours );
                self.currentSelection( SELECTION_HOURS );

            } else if ( self.focusMinutes() ) {
                currentSpinner = getSpinner( self.minutes, MIN_MINUTE_SEC, MAX_MINUTE_SEC, self.focusMinutes );
                self.currentSelection( SELECTION_MINUTES );

            } else if ( self.focusSeconds() ) {
                currentSpinner = getSpinner( self.seconds, MIN_MINUTE_SEC, MAX_MINUTE_SEC, self.focusSeconds);
                self.currentSelection( SELECTION_SECONDS );

            } else if ( self.focusMeridiem() ) {
                currentSpinner = getSpinner( self.meridiem, MIN_MERIDIEM, MAX_MERIDIEM, self.focusMeridiem, VALUES_MERIDIEM );
                self.currentSelection( SELECTION_AM_PM );

            } else {
                currentSpinner = null;
                self.currentSelection(null);
            }
        });

        /**
         * True if each visible input has a value
         */
        self.hasAllValues = self.computed(function(){
            var hours    = parseInt(self.hours(),   10),
                minutes  = parseInt(self.minutes(), 10),
                seconds  = parseInt(self.seconds(), 10),
                meridiem = self.meridiem(),

                hoursOK    = !isNaN(hours),
                minutesOK  = !isNaN(minutes),
                secondsOK  = !self.showSeconds() || !isNaN(seconds),
                meridiemOK = !self.showMeridiem() || !(_.isNull(meridiem) || _.isUndefined(meridiem));

            return hoursOK && minutesOK && secondsOK && meridiemOK;
        });

        /**
         * True if there are any inputs focused in the time widget
         */
        self.hasInnerFocus = self.computed( function(){
            var hasInnerFocus =
                self.focusHours()    ||
                self.focusMinutes()  ||
                self.focusSeconds()  ||
                self.focusMeridiem();

            if ( !hasInnerFocus ) {
                _.delay( clearedClickedObservable, 1000 );
            }

            return hasInnerFocus;
        });

        /**
         * Toggles visible for the value spinners
         */
        self.showSpinners = self.computed(function(){
            return self.hasInnerFocus();
        });

        /**
         * Toggles visible for the clear button
         */
        self.showClearInput = self.computed(function(){
            return self.overClear() && self.hasAllValues() && !self.hasInnerFocus() && self.allowClear();
        });

        /**
         * Handles key interactions in contexts where the keycode is not enough to determine the user's intention.
         * For instance - when a user clicks a localized version of AM or PM like "ص" or "م"
         */
        self.onInputKeyPress = function( model, event ){
            if ( !self.isDisabled() || !self.focusMeridiem() ) {
                var keyPressed = event.which;

                if (handleClearFocused(keyPressed)) {
                    return false;
                }

                handleKeyInteraction(-1, null, getMeridiemForKeyPressed(keyPressed));
            }
            return false;
        };

        /**
         * Responds to the input elements keyDown event to ensure only valid entries make it in, to change focus when
         * the entry exceeds the limit for an entry type, and to respond to clicking of directional buttons to change
         * the currently focused value.
         *
         * @param model
         * @param event
         * @returns {boolean}
         */
        self.onInputKeyDown = function( model, event ){
            if ( !self.isDisabled() ) {
                var keyPressed = event.which,
                    direction, keyValue, meridiem;

                if ( keyPressed === keyCode.TAB ) {
                    if ( self.focusHours() ) {
                        clickedObservable = self.minutes;
                    } else if ( self.focusMinutes() ) {
                        clickedObservable = self.showSeconds() ? self.seconds : self.meridiem;
                    } else if ( self.focusSeconds() ) {
                        clickedObservable = self.meridiem;
                    } else {
                        self.focusMeridiem(false);
                    }
                    return true;
                }

                if ( handleClearFocused( keyPressed ) ) {
                    return false;
                }

                direction = getDirectionForKeyPressed( keyPressed );

                keyValue = keyCode.NUM_KEYS.indexOf( keyPressed );
                keyValue = keyValue === -1 ? keyCode.NUM_PAD_KEYS.indexOf( keyPressed ) : keyValue;
                meridiem = keyValue === -1 && _.isUndefined( direction ) && !self.focusMeridiem() ? getMeridiemForKeyPressed( keyPressed ) : null;

                if ( keyValue === -1 && _.isUndefined( direction ) && ( self.focusMeridiem() || meridiem === null) ) {
                    return true;
                }

                handleKeyInteraction( keyValue, direction, meridiem);
            }
            return false;
        };

        /**
         * Sets the current focus to the clicked input.  Key presses and spinner interaction only effect the focused
         * input.  Spinners are only visible when mousing over the spinner area with an input focused.
         */
        self.onInputClick = function(){
            if ( !self.isDisabled() ){
                if ( self.focusHours() ) {
                    clickedObservable = self.hours;
                } else if ( self.focusMinutes() ) {
                    clickedObservable = self.minutes;
                } else if ( self.focusSeconds() ) {
                    clickedObservable = self.seconds;
                } else if ( self.focusMeridiem() ) {
                    clickedObservable = self.meridiem;
                }
            }

        };

        /**
         * If the outer widget is clicked, the hour input is selected by default
         */
        self.onTimeInputWidgetFocus = function(){
            if ( !self.hasInnerFocus() && !self.isDisabled() ) {
                clickedObservable = self.hours;
                self.focusHours(true);
            }
        };

        /**
         * Clears all the values for time
         * @returns {boolean}
         */
        self.onClearInputClick = function(){
            if ( !self.isDisabled() ) {
                self.hours(    null );
                self.minutes(  null );
                self.seconds(  null );
                self.meridiem( null );
            }
            return false;
        };

        self.onTimeInputWidgetMouseOver = function(){
            if ( !self.isDisabled() && !self.showSpinners() ) {
                self.overClear(true);
            }
        };
        self.onTimeInputWidgetMouseOut = function(){
            self.overClear(false);
        };

        self.onSpinUpMouseDown = function(){
            if ( !self.isDisabled() ) {
                spin(1);
            }
            return false;
        };

        self.onSpinDownMouseDown = function(){
            if ( !self.isDisabled() ) {
                spin(-1);
            }
            return false;
        };

        self.onInputMouseWheel = function(model, e ) {
            if ( !self.isDisabled() ) {
                spin(e.originalEvent.deltaY > 0 ? 1 : -1);
            }
            return false;
        };

        self.onDispose = function(){
            disposeSubscriptions();
        };

        self.configure = function( config ){
            updateFromConfigObject( config );
            configureFromTimeValue( options.value() );
        };

        self.configure( options );

    });

});

